import { useEffect, useRef } from 'react'

type Props = React.VideoHTMLAttributes<HTMLVideoElement> & {
  src: string
}

/**
 * Plays an HLS playlist in any browser. Native HLS (Safari/iOS) gets the
 * playlist URL straight on the <source>; everywhere else we lazy-import
 * hls.js the first time the component mounts.
 */
export function HlsVideo({ src, ...rest }: Props) {
  const ref = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (el.canPlayType('application/vnd.apple.mpegurl')) {
      el.src = src
      return
    }
    let hls: { destroy: () => void } | null = null
    let cancelled = false
    import('hls.js').then(({ default: Hls }) => {
      if (cancelled || !ref.current) return
      if (!Hls.isSupported()) {
        // last-resort fallback — many browsers will still play the playlist
        // when given the URL directly even without explicit support flag
        el.src = src
        return
      }
      const h = new Hls({ enableWorker: true, lowLatencyMode: false })
      h.loadSource(src)
      h.attachMedia(el)
      hls = h
    })
    return () => {
      cancelled = true
      if (hls) hls.destroy()
    }
  }, [src])

  return <video ref={ref} {...rest} />
}
